import { useState, useEffect } from "react";
import React from 'react';
import { useLocation } from "react-router-dom";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  Container,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import Header from "components/Headers/Header.js";
import { clientmanageService } from "../../services/clientmanageService.js";
import { taskmanageService } from "../../services/taskmanageService.js";
import { toast } from 'react-toastify';
import 'assets/css/themecustom-style.css';

const CCRPUpload = () => {
  const location = useLocation();
  const [taskSearch, setTaskSearch] = useState('');
  const [taskList, setTaskList] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [phaseList, setPhaseList] = useState([]);
  const [selectedPhase, setSelectedPhase] = useState('');
  const [rejectionType, setRejectionType] = useState('qa');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [taskLocked, setTaskLocked] = useState(false);

  useEffect(() => {
    const state = location.state;
    if (state && state.task) {
      const task = state.task;
      setSelectedTask(task);
      setTaskSearch(task.name + (task.sub_client ? ' - ' + task.sub_client : ''));
      setTaskLocked(true);
      if (state.phase_id) {
        setSelectedPhase(String(state.phase_id));
      }
    }
  }, []);

  // The valid Category (phase) set depends on the rejection type: QA can use ANY
  // active phase, phase/AuditPod rejections are limited to the job's assigned phases.
  // Refetch whenever either the selected job or the rejection type changes so the
  // reference panel (and the template the user downloads next) stay accurate.
  useEffect(() => {
    if (!selectedTask) {
      setPhaseList([]);
      return;
    }
    clientmanageService.ccrpJobPhases(selectedTask.id, rejectionType).then((response) => {
      setPhaseList(response && response.data && response.data.data ? response.data.data : []);
    });
  }, [selectedTask, rejectionType]);

  function searchTasks(e) {
    const val = e.target.value;
    setTaskSearch(val);
    setSelectedTask(null);
    setPhaseList([]);
    setSelectedPhase('');
    if (val.length >= 1) {
      taskmanageService.getcompanieslistsname(val).then((response) => {
        if (response && response.data && response.data.data) {
          setTaskList(response.data.data);
          setShowDropdown(true);
        }
      });
    } else {
      setTaskList([]);
      setShowDropdown(false);
    }
  }

  function selectTask(task) {
    setSelectedTask(task);
    setTaskSearch(task.name + (task.sub_client ? ' - ' + task.sub_client : ''));
    setTaskList([]);
    setShowDropdown(false);
    setSelectedPhase('');
    // phaseList is refreshed by the [selectedTask, rejectionType] effect.
  }

  function handleFileChange(e) {
    setFile(e.target.files[0]);
  }

  function downloadSample() {
    if (!selectedTask) {
      toast.error('Please select a job/task first — the template is built from the selected job\'s phases.');
      return;
    }
    clientmanageService.ccrpSampleDownload(selectedTask.id, rejectionType);
  }

  function handleImport() {
    if (!selectedTask) {
      toast.error('Please select a job/task.');
      return;
    }
    if (!file) {
      toast.error('Please choose an Excel file to upload.');
      return;
    }

    setLoading(true);
    clientmanageService.ccrpRejectionImport(selectedTask.id, '', rejectionType, file).then((response) => {
      setLoading(false);
      if (response && response.status === 200) {
        toast.success(response.data.message);
        setFile(null);
        document.getElementById('ccrp-file-input').value = '';
      } else {
        const msg = response && response.data && response.data.message
          ? response.data.message
          : 'Import failed. Please check the file and try again.';
        toast.error(msg);
      }
    });
  }

  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        <Row className="mt-5">
          <Col md="8" lg="6">
            <Card className="shadow">
              <CardHeader>
                <h3 className="mb-0">CCRP Rejection Upload</h3>
                <p className="text-muted mb-0" style={{ fontSize: '13px' }}>
                  Upload CCRP rejections in bulk via Excel. Existing checklist data will not be affected.
                </p>
              </CardHeader>
              <CardBody>

                {/* Task Search */}
                <FormGroup>
                  <Label><strong>Select Job / Task</strong></Label>
                  <div style={{ position: 'relative' }}>
                    <Input
                      type="text"
                      placeholder="Type to search job..."
                      value={taskSearch}
                      onChange={searchTasks}
                      onFocus={() => taskList.length > 0 && setShowDropdown(true)}
                      autoComplete="off"
                      disabled={taskLocked}
                      readOnly={taskLocked}
                      style={taskLocked ? { background: '#e9ecef', cursor: 'not-allowed' } : {}}
                    />
                    {taskLocked && (
                      <small className="text-muted" style={{ fontSize: '11px' }}>
                        Job is auto-selected from the dashboard row and cannot be changed.
                      </small>
                    )}
                    {!taskLocked && showDropdown && taskList.length > 0 && (
                      <div style={{
                        position: 'absolute', top: '100%', left: 0, right: 0,
                        background: '#fff', border: '1px solid #ced4da',
                        borderRadius: '4px', zIndex: 1000, maxHeight: '220px', overflowY: 'auto'
                      }}>
                        {taskList.map((task, i) => (
                          <div key={i}
                            style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                            onMouseDown={() => selectTask(task)}
                            className="ccrp-task-option"
                          >
                            <strong>{task.name}</strong>
                            {task.sub_client && <span className="text-muted"> &mdash; {task.sub_client}</span>}
                            {task.id && <span className="text-muted" style={{ fontSize: '12px' }}> (ID: {task.id})</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </FormGroup>

                {/* Rejection Type */}
                <FormGroup>
                  <Label><strong>Rejection Type</strong></Label>
                  <div>
                    <label style={{ marginRight: '20px', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="rejectionType"
                        value="qa"
                        checked={rejectionType === 'qa'}
                        onChange={() => { setRejectionType('qa'); setSelectedPhase(''); }}
                        style={{ marginRight: '6px' }}
                      />
                      QA Rejection
                    </label>
                    <label style={{ cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="rejectionType"
                        value="phase"
                        checked={rejectionType === 'phase'}
                        onChange={() => setRejectionType('phase')}
                        style={{ marginRight: '6px' }}
                      />
                      Phase / AuditPod Rejection
                    </label>
                  </div>
                </FormGroup>

                {/* Sample Download */}
                <FormGroup>
                  <Label><strong>Excel Template</strong></Label>
                  <div>
                    <Button color="secondary" size="sm" type="button" onClick={downloadSample} disabled={!selectedTask}>
                      <i className="ni ni-cloud-download-95 mr-1"></i> Download Sample Template
                    </Button>
                    <p className="text-muted mt-1" style={{ fontSize: '12px' }}>
                      Columns: Category | Sub Category | Feedback | Amount | Type 1 | Type 2.
                      Select a job first — the Category dropdown is filled with {rejectionType === 'qa'
                        ? 'all active phases (QA can reference any phase).'
                        : "that job's assigned phases."}
                    </p>
                  </div>
                </FormGroup>

                {/* File Upload */}
                <FormGroup>
                  <Label><strong>Upload Filled Excel</strong></Label>
                  <Input
                    id="ccrp-file-input"
                    type="file"
                    accept=".xls,.xlsx"
                    onChange={handleFileChange}
                  />
                </FormGroup>

                {/* Import Button */}
                <Button
                  color="primary"
                  type="button"
                  onClick={handleImport}
                  disabled={loading}
                >
                  {loading ? 'Importing...' : 'Import Rejections'}
                </Button>

              </CardBody>
            </Card>
          </Col>

          {/* Info panel */}
          <Col md="4" lg="4">
            <Card className="shadow">
              <CardHeader><h5 className="mb-0">Category Reference (Phases)</h5></CardHeader>
              <CardBody style={{ maxHeight: '420px', overflowY: 'auto', fontSize: '13px' }}>
                {selectedTask ? (
                  phaseList.length > 0 ? (
                    <>
                      <p className="text-muted mb-2">
                        {rejectionType === 'qa' ? (
                          <>These are <strong>all active phases</strong> — a QA rejection can use any of them as its
                          <strong> Category</strong>.</>
                        ) : (
                          <>These are the phases <strong>assigned to this job</strong> — the only valid
                          <strong> Category</strong> values.</>
                        )} The downloaded template's Category dropdown contains exactly these.
                      </p>
                      <ul style={{ paddingLeft: '16px', margin: 0 }}>
                        {phaseList.map((phase, i) => (
                          <li key={i}><strong>{phase.desc}</strong></li>
                        ))}
                      </ul>
                    </>
                  ) : (
                    <p className="text-muted">{rejectionType === 'qa'
                      ? 'No active phases found.'
                      : "No phases are assigned to the selected job, so there's nothing to import against."}</p>
                  )
                ) : (
                  <p className="text-muted">Select a job/task to see its phases (which act as Category values).</p>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default CCRPUpload;
